<template>
  <div :class="!signedIn ? 'sticky-top' : undefined">
    <nav id="main-navigation">
      <div class="py-0 flex-nowrap navbar">
        <a :href="isLogged ? '/dashboard' : '/'">
          <img alt="arztpool.de" height="36" src="/images/arztpool.svg?url" />
        </a>
        <template v-if="!signedIn">
          <div v-if="!smAndDown" class="links">
            <a href="https://arztpool.de/praxen-mvz">{{ $t('NAVIGATION.MENU.LINKS.PRACTICES') }}</a>
            <a href="https://arztpool.de/vertreter">{{ $t('NAVIGATION.MENU.LINKS.REPRESENTATIVES') }}</a>
            <a href="https://arztpool.de/ueber-uns">{{ $t('NAVIGATION.MENU.LINKS.ABOUT_US') }}</a>
            <a href="https://arztpool.de/faq">{{ $t('NAVIGATION.MENU.LINKS.FAQ') }}</a>
          </div>
          <div class="buttons">
            <button @click="showRegistration">{{ $t('NAVIGATION.MENU.REGISTER') }}</button>
            <router-link v-if="!isLoginPage" to="/">{{ $t('NAVIGATION.MENU.LOGIN') }}</router-link>
          </div>
          <MyDoctorPool v-show="myDoctorPoolActive" @showLogin="showLogin" />
        </template>
        <template v-else>
          <div v-if="!hideNavigation" class="navigation" @mouseleave="!smAndDown && hideAllMenus()">
            <div v-if="!isOperator && !smAndDown">
              <v-list>
                <v-list-item>
                  <router-link to="/dashboard">
                    {{ $t('NAVIGATION.MENU.LINKS.DASHBOARD') }}
                  </router-link>
                </v-list-item>
                <v-list-item>
                  <router-link to="/services" :class="{ 'router-link-active': $route.path === '/service' }">
                    {{ $t('NAVIGATION.MENU.LINKS.SERVICES') }}
                  </router-link>
                </v-list-item>
                <v-list-item>
                  <router-link to="/documents">
                    {{ $t('NAVIGATION.MENU.LINKS.DOCUMENTS') }}
                  </router-link>
                </v-list-item>
                <v-list-item class="item-icon">
                  <router-link to="/messages" class="text-none" stacked>
                    <v-badge v-if="unconfirmedMessages.length > 0 && !messagesLoading" :content="unconfirmedMessages.length">
                      <v-icon icon="far fa-bell" size="small" />
                    </v-badge>
                    <span v-else-if="unconfirmedMessages.length === 0 || messagesLoading">
                      <v-icon icon="far fa-bell" size="small" />
                    </span>
                  </router-link>
                </v-list-item>
              </v-list>
            </div>
            <div v-else-if="isOperator && !smAndDown">
              <div class="menu-general" @mouseover="showPublicMenu" @click.stop="togglePublicMenu">
                {{ $t('NAVIGATION.MENU.TITLE') }}
                <v-icon icon="fal fa-bars" />
              </div>
            </div>
            <div
              v-if="!smAndDown"
              :class="{ 'signed-in': signedIn, active: myDoctorPoolActive }"
              class="btn-my-doctor-pool"
              @click="dashboardRedirect"
              @mouseover="showMyDoctorPoolMenu"
            >
              {{ $t('NAVIGATION.MY_DOCTOR_POOL.TITLE') }}
              <v-icon icon="fal fa-user-md" />
            </div>
            <button v-if="smAndDown" class="mobile-hamburger" @click="toggleMobileMenu">
              <v-icon :icon="mobileMenuOpen ? 'fal fa-times' : 'fal fa-bars'" size="large" />
            </button>
            <MyDoctorPool v-show="myDoctorPoolActive && !smAndDown" @showLogin="showLogin" />
            <PublicMenu v-show="publicMenuActive && !smAndDown" @showLogin="showLogin" />
          </div>
        </template>
      </div>
    </nav>
  </div>
  <v-navigation-drawer v-if="signedIn && smAndDown" v-model="mobileMenuOpen" temporary location="right" width="300" class="mobile-drawer">
    <template #prepend>
      <div class="drawer-header">
        <img alt="arztpool.de" src="/images/arztpool-solid-white.svg?url" height="36" />
      </div>
    </template>

    <v-list density="compact" nav class="pt-3">
      <v-list-subheader class="drawer-subheader">
        {{ isOperator ? 'Verwaltung' : $t('NAVIGATION.MY_DOCTOR_POOL.TITLE') }}
      </v-list-subheader>
      <template v-if="!isOperator">
        <v-list-item to="/dashboard" prepend-icon="far fa-tachometer-alt">
          <v-list-item-title>{{ $t('NAVIGATION.MY_DOCTOR_POOL.LINKS.DASHBOARD') }}</v-list-item-title>
        </v-list-item>
        <v-list-item :to="userType === 'partner' ? '/services/overview' : '/services/marketplace'" prepend-icon="fas fa-stethoscope">
          <v-list-item-title>{{ $t('NAVIGATION.MY_DOCTOR_POOL.LINKS.SERVICES') }}</v-list-item-title>
        </v-list-item>
        <v-list-item to="/documents" prepend-icon="far fa-file-alt">
          <v-list-item-title>{{ $t('NAVIGATION.MY_DOCTOR_POOL.LINKS.DOCUMENTS') }}</v-list-item-title>
        </v-list-item>
        <v-list-item to="/messages" prepend-icon="far fa-envelope">
          <v-list-item-title>
            {{ $t('NAVIGATION.MY_DOCTOR_POOL.LINKS.MESSAGES') }}
            <v-chip v-if="unconfirmedMessages.length > 0 && !messagesLoading" color="#ed6d05" size="x-small" class="ml-2">
              {{ unconfirmedMessages.length }}
            </v-chip>
          </v-list-item-title>
        </v-list-item>
      </template>
      <template v-else>
        <v-list-item :to="{ name: 'OperatorServices' }" prepend-icon="fal fa-user-md">
          <v-list-item-title>{{ $t('OPERATORS.NAVIGATION.SERVICES') }}</v-list-item-title>
        </v-list-item>
        <v-list-item :to="{ name: 'OperatorTierServices' }" prepend-icon="fal fa-user-md">
          <v-list-item-title>{{ $t('OPERATORS.NAVIGATION.TIER_SERVICES') }}</v-list-item-title>
        </v-list-item>
        <v-list-item :to="{ name: 'OperatorServiceSubmissions' }" prepend-icon="fas fa-plus-circle">
          <v-list-item-title>{{ $t('OPERATORS.NAVIGATION.SERVICE_SUBMISSIONS') }}</v-list-item-title>
        </v-list-item>
        <v-list-item :to="{ name: 'OperatorUsers' }" prepend-icon="fas fa-users">
          <v-list-item-title>{{ $t('OPERATORS.NAVIGATION.USERS') }}</v-list-item-title>
        </v-list-item>
        <v-list-item :to="{ name: 'OperatorServiceAreas' }" prepend-icon="fal fa-map">
          <v-list-item-title>{{ $t('OPERATORS.NAVIGATION.SERVICEAREAS') }}</v-list-item-title>
        </v-list-item>
        <v-list-item :to="{ name: 'OperatorAccounting' }" prepend-icon="fas fa-money-bill">
          <v-list-item-title>{{ $t('OPERATORS.NAVIGATION.ACCOUNTING') }}</v-list-item-title>
        </v-list-item>
        <v-list-item :to="{ name: 'RegisteredServices' }" prepend-icon="fas fa-glasses">
          <v-list-item-title>Eingetragene Dienste</v-list-item-title>
        </v-list-item>
        <v-list-item :to="{ name: 'RegisteredUsers' }" prepend-icon="fas fa-glasses">
          <v-list-item-title>Registrierte Nutzer</v-list-item-title>
        </v-list-item>
        <v-list-item :to="{ name: 'LogisticsHub' }" prepend-icon="fas fa-truck">
          <v-list-item-title>{{ $t('OPERATORS.NAVIGATION.LOGISTICS_HUB') }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>

    <v-divider class="drawer-divider" />

    <v-list density="compact" nav>
      <v-list-subheader class="drawer-subheader">Dienste</v-list-subheader>
      <v-list-item to="/kv-dienst-vertreten-lassen" prepend-icon="fas fa-clinic-medical">
        <v-list-item-title>{{ $t('NAVIGATION.MY_DOCTOR_POOL.LINKS.PROVIDE_SERVICE') }}</v-list-item-title>
      </v-list-item>
      <v-list-item to="/kv-dienst-uebernehmen" prepend-icon="fas fa-clinic-medical">
        <v-list-item-title>{{ $t('NAVIGATION.MY_DOCTOR_POOL.LINKS.REPRESENT_SERVICE') }}</v-list-item-title>
      </v-list-item>
      <v-list-item to="/mvz" prepend-icon="fas fa-clinic-medical">
        <v-list-item-title>{{ $t('NAVIGATION.MY_DOCTOR_POOL.LINKS.MVZ') }}</v-list-item-title>
      </v-list-item>
    </v-list>

    <template v-if="!isOperator">
      <v-divider class="drawer-divider" />
      <v-list density="compact" nav>
        <v-list-subheader class="drawer-subheader">Konto</v-list-subheader>
        <v-list-item :to="{ name: 'Account' }" prepend-icon="fas fa-user">
          <v-list-item-title>{{ $t('NAVIGATION.MY_DOCTOR_POOL.LINKS.SUBSTITUTES.YOUR_ACCOUNT') }}</v-list-item-title>
        </v-list-item>
        <v-list-item :to="{ name: 'BaseData' }" prepend-icon="fas fa-tasks">
          <v-list-item-title>{{ $t('NAVIGATION.MY_DOCTOR_POOL.LINKS.SUBSTITUTES.BASIC_DATA') }}</v-list-item-title>
        </v-list-item>
        <template v-if="isSubstitute">
          <v-list-item :to="{ name: 'SubstituteBilling' }" prepend-icon="fas fa-book">
            <v-list-item-title>{{ $t('NAVIGATION.MY_DOCTOR_POOL.LINKS.SUBSTITUTES.BILLING_DATA') }}</v-list-item-title>
          </v-list-item>
          <v-list-item :to="{ name: 'SubstituteInsurances' }" prepend-icon="fas fa-clinic-medical">
            <v-list-item-title>{{ $t('NAVIGATION.MY_DOCTOR_POOL.LINKS.SUBSTITUTES.INSURANCE') }}</v-list-item-title>
          </v-list-item>
          <v-list-item :to="{ name: 'SubstituteSpecialization' }" prepend-icon="fas fa-clinic-medical">
            <v-list-item-title>{{ $t('NAVIGATION.MY_DOCTOR_POOL.LINKS.SUBSTITUTES.HEALTH_INSURANCE') }}</v-list-item-title>
          </v-list-item>
        </template>
        <template v-if="isPartner">
          <v-list-item :to="{ name: 'PartnerInstitutions' }" prepend-icon="fas fa-clinic-medical">
            <v-list-item-title>{{ $t('NAVIGATION.MY_DOCTOR_POOL.LINKS.PARTNER.INSTITUTION') }}</v-list-item-title>
          </v-list-item>
          <v-list-item :to="{ name: 'PartnerEstablishments' }" prepend-icon="fas fa-clinic-medical">
            <v-list-item-title>{{ $t('NAVIGATION.MY_DOCTOR_POOL.LINKS.PARTNER.ESTABLISHMENT') }}</v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </template>

    <div class="drawer-logout">
      <v-divider />
      <div class="pa-3">
        <v-btn variant="flat" color="#ed6d05" block @click="handleMobileLogout">
          <v-icon icon="fas fa-sign-out-alt" class="mr-2" size="small" />
          {{ $t('NAVIGATION.MY_DOCTOR_POOL.LOGOUT') }}
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
  import { defineComponent, onUnmounted, ref, watch } from 'vue';
  import { useDisplay } from 'vuetify';
  import { useRoute } from 'vue-router';
  import { Hub } from 'aws-amplify';
  import { RouterSamePageErrorHandler } from '@/plugins/router';

  import Stores from '@/stores';

  import PublicMenu from '@/components/page/navigation/PublicMenu.vue';
  import MyDoctorPool from '@/components/page/navigation/MyDoctorPool.vue';
  import EventBus from '@/services/EventBus';

  export default defineComponent({
    name: 'PageNavigation',
    components: {
      PublicMenu,
      MyDoctorPool,
    },
    setup() {
      const { signedIn, userType } = Stores.userDataStoreToRefs();
      const { isOperator, isSubstitute, isPartner } = Stores.profileDataStoreToRefs();
      const { unconfirmedMessages } = Stores.messageStoreToRefs();

      const { smAndDown } = useDisplay();
      const route = useRoute();

      const hideNavigation = ref(false);
      const mobileMenuOpen = ref(false);

      watch(
        () => route.path,
        (path) => {
          hideNavigation.value = path === '/wizard';
          mobileMenuOpen.value = false;
        },
        {
          immediate: true,
        },
      );

      watch(mobileMenuOpen, (isOpen) => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
      });

      onUnmounted(() => {
        document.body.style.overflow = '';
      });

      return {
        signedIn,
        userType,
        isOperator,
        isSubstitute,
        isPartner,
        smAndDown,
        hideNavigation,
        mobileMenuOpen,
        unconfirmedMessages,
        publicMenuActive: ref(false),
        myDoctorPoolActive: ref(false),
      };
    },
    mounted() {
      if (this.$route.query.login === 'true') {
        this.showLogin();
      }
    },
    computed: {
      messagesLoading(): boolean {
        return Stores.message.messagesLoading;
      },
      isLogged(): boolean {
        return this.signedIn;
      },
      isLoginPage(): boolean {
        return this.$route.path === '/';
      },
    },
    methods: {
      togglePublicMenu(): void {
        this.publicMenuActive = !this.publicMenuActive;
      },

      showPublicMenu(): void {
        this.publicMenuActive = true;
        this.myDoctorPoolActive = false;
      },

      hidePublicMenu(): void {
        this.publicMenuActive = false;
      },

      showMyDoctorPoolMenu(): void {
        this.publicMenuActive = false;
        this.myDoctorPoolActive = true;
      },

      hideAllMenus(): void {
        this.publicMenuActive = false;
        this.myDoctorPoolActive = false;
      },

      toggleMobileMenu(): void {
        this.mobileMenuOpen = !this.mobileMenuOpen;
      },

      showLogin(): void {
        this.$router.push('/');
      },

      showRegistration(): void {
        EventBus.emit(EventBus.keys.PREREGISTER_OPEN, true);
      },

      async dashboardRedirect(): Promise<void> {
        if (this.signedIn) {
          if (this.isOperator) {
            this.$router.push({ name: 'Operators' }).catch(RouterSamePageErrorHandler);
          } else {
            this.$router.push({ name: 'Dashboard' }).catch(RouterSamePageErrorHandler);
          }
        }
      },

      handleMobileLogout(): void {
        this.mobileMenuOpen = false;
        Stores.userData.logout().then(() => {
          if (Stores.userData.signedIn) {
            Hub.listen('auth', (data) => {
              if (data.payload.event === 'signOut') {
                this.$router.push('/').catch(RouterSamePageErrorHandler);
              }
            });
          } else {
            this.$router.push('/').catch(RouterSamePageErrorHandler);
          }
        });
      },
    },
  });
</script>

<style lang="scss" scoped>
  @import '@/scss/configs/colors';
  .sticky-top {
    position: sticky;
    top: 0;
    z-index: 1000;
  }
  #main-navigation:deep(.v-list-item__content) {
    overflow: visible;
  }
  #main-navigation:deep(.v-badge__badge) {
    border-color: $color-orange !important;
    background-color: $color-orange !important;
  }

  #main-navigation {
    position: relative;
    background-color: $color-white;
    z-index: 94;
    box-shadow: 0 2px 4px 0 rgba($color-black, 0.1);
    height: 5.9rem;

    .navigation-row {
      position: relative;
    }

    .navigation {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding-block-start: 1rem;
      padding-block-end: 1rem;
      font-size: 16px;
      color: $color-marine;
      height: 100%;

      &.sm {
        .svg-inline--fa {
          font-size: 20px;
          margin-right: 10px;
          color: $color-marine;
        }

        a.router-link-active {
          border: none;
        }
      }

      .v-list {
        display: inline-flex;

        .item-icon {
          padding: 0 0 0 12px;
          width: 65px;

          a {
            border-radius: 20px;
            height: 40px;
            width: 40px;
            display: flex;
            justify-content: center;
            align-items: center;
            .v-icon {
              color: $color-marine;
            }

            &.router-link-active {
              border-bottom-color: transparent;
              width: 40px;
              display: flex;
              justify-content: center;
              align-items: center;
              background-color: rgba($color-orange, 0.15) !important;
            }
          }
        }

        a {
          font-size: 20px;
          font-weight: bolder;
          color: $color-marine !important;
          text-decoration: none;
          border-bottom: 2px solid transparent;

          &.router-link-active {
            border-bottom-color: $color-orange;
            width: fit-content;
            display: inline-block;
          }
        }
      }

      .menu-general,
      .btn-my-doctor-pool {
        display: flex;
        align-items: center;
        user-select: none;

        svg {
          margin-left: 16px;
          font-size: 24px;
        }

        &:hover {
          cursor: pointer;
        }
      }

      .menu-general {
        font-weight: 600;
      }

      .btn-my-doctor-pool {
        border: 1px solid $color-marine;
        border-radius: 4px;
        padding: 12px 32px;
        background-color: $color-white;
        color: $color-marine;
        margin-left: 60px;

        &.signed-in {
          border-color: transparent;
          background-color: $color-marine;
          color: $color-white;
        }

        &:hover,
        &.active {
          border-color: transparent;
          background-color: $color-blue-grey-30;
          color: $color-marine;
        }
      }

      .mobile-hamburger {
        background: none;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        color: $color-marine;
        padding: 8px;
      }
    }
  }

  .navbar {
    height: 100%;
    margin-inline: clamp(1rem, 5vw, 5rem);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .links {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
    a {
      color: $color-marine;

      &:hover {
        cursor: pointer;
      }
    }
  }

  .buttons {
    display: flex;
    gap: 1rem;
    margin-left: 12px;

    a,
    button {
      color: $color-white;
      font-size: 0.875rem;
      text-transform: none;
      background-color: $color-marine;
      border-radius: 0.25rem;
      padding-block: 0.55rem;
      padding-inline: 2rem;

      &:hover {
        color: $color-marine-dark;
        background-color: $color-orange;
      }
    }
  }
</style>

<style lang="scss">
  @import '@/scss/configs/colors';

  .mobile-drawer {
    background-color: $color-white !important;

    .drawer-header {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1.5rem 1rem 1.25rem;
      background-color: $color-marine;
      border-bottom: 1px solid $color-blue-grey-30;

      img {
        height: 36px;
        max-width: 100%;
      }
    }

    .v-list {
      background: transparent;
      padding-block: 2px;
    }

    .v-list-item {
      color: $color-marine;
      border-radius: 4px;
      margin-block: 1px;
      border-left: 3px solid transparent;

      .v-icon {
        color: $color-marine;
      }

      &:hover {
        background-color: rgba($color-orange, 0.06) !important;
        color: $color-marine;
      }

      &.v-list-item--active {
        background-color: rgba($color-orange, 0.08) !important;
        color: $color-marine !important;
        font-weight: 700;
        border-left-color: $color-orange;

        .v-icon {
          color: $color-orange !important;
        }
      }
    }

    .v-list-item-title {
      font-size: 1rem;
      font-weight: 600;
    }

    .drawer-subheader {
      color: $color-blue-grey-60 !important;
      font-size: 0.68rem !important;
      font-weight: 700 !important;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      min-height: 20px;
    }

    .drawer-divider {
      border-color: $color-blue-grey-20 !important;
      margin-inline: 0.75rem;
      margin-block: 0;
    }

    .drawer-logout {
      position: sticky;
      bottom: 0;
      background-color: $color-white;
      z-index: 1;

      .v-divider {
        border-color: $color-blue-grey-20 !important;
      }

      .v-btn {
        letter-spacing: normal;
        text-transform: none;
        font-size: 1rem;
      }
    }
  }
</style>
